import { useEffect, useRef } from "react";
import { useMemberService } from "@/hooks/use-member-service";
import { useGroupProvider } from "@/context/group-provider";
import LoadingIndiator from "./components/loading-indiator";
import type { ServiceDetail } from "@/model/serviceDetails";
import PageContainer from "./containers/page-container";
import NoDataFallback from "@/components/ui/fallback-nodata";
import Service from "./components/service/service";
import { Separator } from "@/components/ui/separator";
import Document from "./components/service/document";
import { useCoverageCertificate } from "@/hooks/use-coverage-certificate";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import CoverageCertificate from "./components/service/coverageCertificate";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "./components/error-notification";
import { useNavigate } from "react-router-dom";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useFeatureName } from "@/hooks/use-pageTitle";

const Services = () => {
  const featureName = useFeatureName();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const navigate = useNavigate();
  const { groupData } = useGroupProvider();
  const { addError } = useNotificationError();

  const coverageCertificateQuery = useCoverageCertificate(activeCoverage);

  const serviceQuery = useMemberService();

  const showCertificate = Boolean(
    coverageCertificateQuery.isFetched &&
    coverageCertificateQuery.data?.base64Encoded,
  );

  const hasShownServiceErrorRef = useRef(false);
  const hasNavigatedHomeRef = useRef(false);

  useEffect(() => {
    if (!serviceQuery.isError) {
      hasShownServiceErrorRef.current = false;
      hasNavigatedHomeRef.current = false;
      return;
    }
    if (!hasShownServiceErrorRef.current) {
      hasShownServiceErrorRef.current = true;
      console.error(serviceQuery.error.message);
      addError({
        type: ErrorNotificationType.ERROR,
        message: "Unable to load services.",
      });
    }

    const certificateSettled =
      coverageCertificateQuery.isFetched || coverageCertificateQuery.isError;
    if (
      certificateSettled &&
      !showCertificate &&
      !hasNavigatedHomeRef.current
    ) {
      hasNavigatedHomeRef.current = true;
      navigate(`/${ScreenRouteNames.HOME.path}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serviceQuery.isError,
    showCertificate,
    coverageCertificateQuery.isFetched,
    coverageCertificateQuery.isError,
  ]);

  const hasShownCertificateErrorRef = useRef(false);
  useEffect(() => {
    if (!coverageCertificateQuery.isError) {
      hasShownCertificateErrorRef.current = false;
      return;
    }
    if (hasShownCertificateErrorRef.current) {
      return;
    }
    hasShownCertificateErrorRef.current = true;
    console.error(coverageCertificateQuery.error.message);
    addError({
      type: ErrorNotificationType.ERROR,
      message: "Unable to load coverage certificate.",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverageCertificateQuery.isError]);

  if (serviceQuery.isLoading || coverageCertificateQuery.isLoading) {
    return <LoadingIndiator />;
  }

  const hasNoData = Boolean(
    serviceQuery.data?.serviceDetails &&
    serviceQuery.data?.serviceDetails.length === 0,
  );

  const showDocument = Boolean(
    serviceQuery.data &&
    serviceQuery.data?.serviceDetails.filter(
      (service) => service.category === "Document",
    ).length > 0,
  );

  return (
    <PageContainer title={featureName}>
      {hasNoData ? (
        <NoDataFallback title="Services" />
      ) : (
        <>
          <div className="grid w-full grid-cols-1 gap-4 p-6 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
            {serviceQuery.data &&
            serviceQuery.data?.serviceDetails.filter(
              (service) => service.category !== "Document",
            ).length > 0 ? (
              serviceQuery.data?.serviceDetails
                .filter((service) => service.category !== "Document")
                .map((service: ServiceDetail, index: number) => {
                  return <Service key={index} {...service} />;
                })
            ) : (
              <NoDataFallback title="Services" />
            )}
          </div>

          <Separator />

          <div className="grid w-full grid-cols-1 gap-4 p-6 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
            {showDocument && showCertificate ? (
              <>
                {serviceQuery.data?.serviceDetails
                  .filter((service) => service.category === "Document")
                  .map((service: ServiceDetail, index: number) => {
                    return (
                      <Document
                        key={index}
                        document={service}
                        groupId={groupData?.group_id ?? ""}
                      />
                    );
                  })}

                <CoverageCertificate
                  title={coverageCertificateQuery.data?.title}
                  base64Pdf={coverageCertificateQuery.data?.base64Encoded ?? ""}
                />
              </>
            ) : showDocument ? (
              serviceQuery.data?.serviceDetails
                .filter((service) => service.category === "Document")
                .map((service: ServiceDetail, index: number) => {
                  return (
                    <Document
                      key={index}
                      document={service}
                      groupId={groupData?.group_id ?? ""}
                    />
                  );
                })
            ) : showCertificate ? (
              <CoverageCertificate
                title={coverageCertificateQuery.data?.title}
                base64Pdf={coverageCertificateQuery.data?.base64Encoded ?? ""}
              />
            ) : (
              <NoDataFallback title="Documents" />
            )}
          </div>
        </>
      )}
    </PageContainer>
  );
};

export default Services;
